import { Box, Button, OutlinedInput, Stack, Typography, useTheme } from '@mui/material';
import { confirmSignIn, signIn, signInWithRedirect } from 'aws-amplify/auth';
import type { SignInOutput } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import parsePhoneNumber from 'libphonenumber-js';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import logo from '../../../assets/caloLogo';
import { caloTheme } from '../../../assets/themes/calo';
import { ChallengeType } from '../../../libs';

interface CustomSignInProps {
  onSignedIn: () => void;
}

Hub.listen('auth', ({ payload }) => {
  if (payload.event === 'signInWithRedirect_failure') {
    toast.error('An error occured while signing you in through your google account please try again');
  }
});

export default function CustomSignIn({ onSignedIn }: CustomSignInProps) {
  const theme = useTheme();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (!resendIn) return;

    const token = setInterval(() => {
      setResendIn((prev) => prev - 1);
    }, 1000);

    return () => {
      clearInterval(token);
    };
  }, [resendIn]);

  const resendInPadded = useMemo(
    () =>
      resendIn &&
      `${Math.floor(resendIn / 60)
        .toString()
        .padStart(2, '0')}:${(resendIn % 60).toString().padStart(2, '0')}`,
    [resendIn]
  );

  const applyResendIn = (nextStep: SignInOutput['nextStep']) => {
    if (nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') return;

    const resendIn = parseInt(nextStep.additionalInfo?.resendIn ?? '');
    setResendIn(Number.isNaN(resendIn) ? 30 : resendIn);
  };

  const sendSignInOTP = async () => {
    try {
      if (!phoneNumber.length) {
        toast.error('Phone Number is required');
        return;
      }

      const parsedPhoneNumber = parsePhoneNumber(phoneNumber);

      if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) {
        toast.error('Please enter a valid phone number');
        return;
      }

      setIsLoading(true);

      const { nextStep } = await signIn({
        username: phoneNumber,
        options: { authFlowType: 'CUSTOM_WITHOUT_SRP' }
      });

      setCodeSent(true);
      applyResendIn(nextStep);

      return true;
    } catch (error) {
      const name = error instanceof Error ? error.name : '';

      if (name === 'UserAlreadyAuthenticatedException') {
        onSignedIn();
        return;
      }

      toast.error('Sign-in failed. Check the phone number and try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async () => {
    try {
      setIsLoading(true);

      if (!otp.length) {
        toast.error('OTP is required');
        return;
      }

      const { isSignedIn } = await confirmSignIn({
        challengeResponse: otp.trim(),
        options: { clientMetadata: { challengeType: ChallengeType.OTP } }
      });

      if (!isSignedIn) {
        toast.error('An error occured while verifying the OTP please enter the correct code');
        return;
      }

      onSignedIn();
    } catch {
      toast.error('An error occured while verifying the OTP please enter the correct code');
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      setIsLoading(true);

      const { nextStep } = await confirmSignIn({
        challengeResponse: 'RESEND_OTP',
        options: { clientMetadata: { challengeType: ChallengeType.RESEND_OTP } }
      });

      applyResendIn(nextStep);
    } catch {
      toast.error('An error occured while resending the OTP please try again');
    } finally {
      setIsLoading(false);
    }
  };

  async function handleSignInClick() {
    try {
      setIsLoading(true);

      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      if (error instanceof Error && error.name === 'UserAlreadyAuthenticatedException') {
        onSignedIn();
        return;
      }
      toast.error('An error occured while signing you in please try again');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Stack
      direction={'column'}
      sx={{
        alignSelf: 'center',
        alignItems: 'center',
        width: '30rem',

        [theme.breakpoints.down('sm')]: {
          width: '100%'
        }
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          padding: '20px'
        }}
      >
        <img alt="Calo logo" src={`data:image/png;base64, ${logo}`} />
      </Box>
      <Stack
        direction={'column'}
        sx={{
          rowGap: '15px',
          padding: '20px',
          border: '1px solid black'
        }}
      >
        <Stack
          direction={'column'}
          sx={{
            rowGap: '15px',
            textAlign: 'center'
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontFamily: caloTheme.typography.fontFamily,
              fontWeight: '500',
              textAlign: 'center',
              fontSize: '33px'
            }}
          >
            Sign in to your account
          </Typography>
          <Button loading={isLoading} variant="outlined" onClick={() => void handleSignInClick()}>
            Google Sign In
          </Button>
          <Typography
            sx={{
              color: '#9D9D9D',
              paddingBottom: '0px'
            }}
          >
            Phone number must start with the country code and follow the format +xxxxxxxxxx
          </Typography>
        </Stack>
        <Stack
          direction="column"
          sx={{
            rowGap: '15px'
          }}
        >
          <Stack
            direction="column"
            sx={{
              rowGap: '10px'
            }}
          >
            <OutlinedInput
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter your phone number with country code"
            />
            {!codeSent && (
              <Button loading={isLoading} variant="contained" onClick={() => void sendSignInOTP()}>
                Continue
              </Button>
            )}
          </Stack>
          {codeSent && (
            <Stack
              direction="column"
              sx={{
                rowGap: '10px'
              }}
            >
              <OutlinedInput value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Code" />
              <Button loading={isLoading} variant="contained" onClick={() => void verifyOtp()}>
                Verify
              </Button>
              {resendIn ? (
                <Typography
                  sx={{
                    color: '#9D9D9D',
                    paddingBottom: '0px'
                  }}
                >
                  Resend code in {resendInPadded}
                </Typography>
              ) : (
                <Box>
                  <Typography
                    sx={{
                      color: '#9D9D9D',
                      paddingBottom: '0px'
                    }}
                  >
                    Didn't Recive it? Retry
                  </Typography>
                  <Button loading={isLoading} variant="contained" onClick={() => void resendOtp()}>
                    Resend
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
      <Box
        sx={{
          textAlign: 'center',
          padding: '20px'
        }}
      >
        <Typography
          sx={{
            color: '#57AE7F'
          }}
        >
          &copy; 2022 Calo Inc.
        </Typography>
      </Box>
    </Stack>
  );
}
